/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Stripe Payment & Checkout Session Schemas & DTOs
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Request schema for creating a Stripe Checkout Session.
 */
export const CreateCheckoutSessionSchema = z
  .object({
    planId: z.string().min(1, 'Plan ID is required').openapi({ description: 'ID of subscription plan or token pack', example: 'plan_pro_yearly' }),
    successUrl: z.string().url('Invalid success URL').optional().openapi({ description: 'Redirect URL upon payment success', example: 'https://sophiaplatform.com/checkout/success' }),
    cancelUrl: z.string().url('Invalid cancel URL').optional().openapi({ description: 'Redirect URL upon payment cancellation', example: 'https://sophiaplatform.com/checkout/cancel' }),
    topupCredits: z.number().positive().optional().openapi({ description: 'Custom topup credit volume if prepaid pack', example: 50000 }),
    orderId: z.string().optional().openapi({ description: 'Tracking Order ID for D1 ledger alignment' }),
  })
  .openapi('CreateCheckoutSessionRequest');

export type CreateCheckoutSessionDto = z.infer<typeof CreateCheckoutSessionSchema>;

/**
 * Response schema returned upon successful Checkout Session creation.
 */
export const CheckoutSessionResponseSchema = z
  .object({
    sessionId: z.string().openapi({ description: 'Stripe Checkout Session ID', example: 'cs_test_a1b2c3d4e5' }),
    checkoutUrl: z.string().nullable().openapi({ description: 'Hosted Stripe Checkout redirection URL', example: 'https://checkout.stripe.com/c/pay/cs_test_a1b2c3d4e5' }),
    mode: z.enum(['subscription', 'payment']).openapi({ description: 'Checkout mode', example: 'subscription' }),
    planId: z.string().openapi({ description: 'Referenced plan ID', example: 'plan_pro_yearly' }),
    planName: z.string().openapi({ description: 'Friendly plan name', example: 'Pro Creator Yearly' }),
    amount: z.number().openapi({ description: 'Total charge amount in smallest currency unit or dollars', example: 2900 }),
    currency: z.string().openapi({ description: 'Three-letter ISO currency code', example: 'usd' }),
  })
  .openapi('CheckoutSessionResponse');

export type CheckoutSessionResponseDto = z.infer<typeof CheckoutSessionResponseSchema>;

